import React, { useState, useEffect } from 'react';
import { Card, Btn, Modal, Input, Select, Textarea, FormGrid, SectionHeader, Badge, StatCard, Table, RNLLoader } from '../components/UI';
import { fmt, fmtDate } from '../utils';
import * as api from '../api';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const STATUS_OPTS = ['planning', 'active', 'on_hold', 'completed', 'cancelled'].map(s => ({ value: s, label: s }));
const CONTRACT_OPTS = ['', ...['lump_sum', 'remeasured', 'cost_plus', 'unit_rate']].map(s => ({ value: s, label: s || '— Select Type —' }));

function ProjectDashboardView({ project, onBack, toast }) {
  const navigate = useNavigate();
  const [costSummary, setCostSummary] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);

    try {
      const sum = await api.getCostSummary(project.project_id).catch(() => null);
      setCostSummary(sum);
    } catch { }

    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [project.project_id]);


  const groups = [
    {
      id: 'project-divisions',
      title: 'Project Divisions',
      icon: '🏢',
      page: 'project-divisions'
    },
    {
      id: 'budget',
      title: 'Project Budget',
      icon: '💹',
      page: 'boq/boqbudgets'
    },
    {
      id: 'boq',
      title: 'BOQ',
      icon: '📊',
      page: 'boq'
    },
    // {
    //   id: 'procurement',
    //   title: 'Procurement Register',
    //   icon: '🛒',
    //   page: 'procurement'
    // },
    {
      id: 'direct-material',
      title: 'Direct Material Register',
      icon: '🧱',
      page: 'direct-material'
    },
    // {
    //   id: 'daily-expense',
    //   title: 'Daily Expense Register',
    //   icon: '💵',
    //   page: 'coming-soon',
    //   tab: '?module=Daily Expense Register'
    // },
    // {
    //   id: 'material-issue',
    //   title: 'Material Issue Register',
    //   icon: '📤',
    //   page: 'coming-soon',
    //   tab: '?module=Material Issue Register'
    // },
    {
      id: 'labour-cost',
      title: 'Labour Cost Register',
      icon: '👷‍♂️',
      page: 'labour'
    },
    {
      id: 'qs-site',
      title: 'QS Site Installation Register',
      icon: '📐',
      page: 'qs-site'
    },
    // {
    //   id: 'installed-material',
    //   title: 'Installed Material Register',
    //   icon: '✅',
    //   page: 'coming-soon',
    //   tab: '?module=Installed Material Register'
    // },
    // {
    //   id: 'transportation',
    //   title: 'Transportation Register',
    //   icon: '🚛',
    //   page: 'coming-soon',
    //   tab: '?module=Transportation Register'
    // },
    // {
    //   id: 'equipment-tools',
    //   title: 'Equipment & Tools Register',
    //   icon: '🚜',
    //   page: 'coming-soon',
    //   tab: '?module=Equipment and Tools Register'
    // },
    // {
    //   id: 'other-direct',
    //   title: 'Other Direct Cost Register',
    //   icon: '📂',
    //   page: 'coming-soon',
    //   tab: '?module=Other Direct Cost Register'
    // },
    // {
    //   id: 'site-issues',
    //   title: 'Site Issues',
    //   icon: '⚡',
    //   page: 'site-issues'
    // },
    // {
    //   id: 'finance-analytics',
    //   title: 'Finance & Analytics',
    //   icon: '💰',
    //   page: 'finance-analytics'
    // },
  ];

  const renderModuleView = () => {
    if (loading) {
      return <RNLLoader title="Loading Modules..." subtitle="Please wait" />;
    }

    return (
      <div className="fade-in">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16 }}>
          {groups.map(g => (
            <Card
              key={g.id}
              className="toggle-on-hover black-border-card"
              onClick={() => {
                if (g.page) {
                  let url = `/projects/${project.project_id}/${g.page}`;
                  if (g.tab) {
                    if (g.tab.startsWith('?')) url += g.tab;
                    else url += `/${g.tab}`;
                  }
                  navigate(url);
                }
              }}
              style={{
                width: '100%',
                padding: '24px 16px',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', textAlign: 'center', gap: 12,
                borderRadius: 12,
                background: 'var(--bg-card)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'border-color 0.2s, transform 0.2s',
                cursor: 'pointer'
              }}
            >
              <span style={{
                fontSize: 32, lineHeight: 1,
                padding: 12, borderRadius: 16,
                background: 'var(--accent-dim)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{g.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                {g.title}
              </span>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div key={project.project_id} className="zoom-in">
      {/* UPPER SECTION */}
      <div style={{ background: '#b5b6b8ff', padding: '24px', borderRadius: 16, border: '3px solid black', marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>{project.project_name}</h2>
              <span style={{ background: project.status === 'active' ? '#10b981' : 'var(--bg-card)', color: project.status === 'active' ? '#000' : 'var(--text-muted)', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {project.status}
              </span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8, fontWeight: 'bold', display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <span>CODE: <strong style={{ fontFamily: 'var(--mono)', color: '#23428aff' }}>{project.project_code}</strong></span>
              {project.client_name && <span>CLIENT: <strong style={{ fontFamily: 'var(--mono)', color: '#23428aff' }}>{project.client_name}</strong></span>}
              {project.location && <span>LOCATION: <strong style={{ fontFamily: 'var(--mono)', color: '#23428aff' }}>{project.location}</strong></span>}
              {(project.start_date || project.end_date) && <span>TIMELINE: <strong style={{ fontFamily: 'var(--mono)', color: '#23428aff' }}>{project.start_date ? fmtDate(project.start_date) : 'TBA'} — {project.end_date ? fmtDate(project.end_date) : 'TBA'}</strong></span>}
            </div>
          </div>
          <Btn variant="secondary" onClick={onBack} style={{ background: '#658ff1ff', borderRadius: '24px', border: '1px solid black' }}>← Back to Projects</Btn>
        </div>

        {/* Financial Health KPIs */}
        <div>
          {loading ? (
            <RNLLoader title="Loading Financials..." subtitle="Please wait" />
          ) : costSummary ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
              <StatCard label="Contract Value" value={fmt(costSummary.contract_value)} color="accent" icon="₦" style={{ border: '1px solid black' }} />
              <StatCard label="Original Budget" value={fmt(costSummary.original_budget)} color="cyan" icon="◎" style={{ border: '1px solid black' }} />
              <StatCard label="Committed" value={fmt(costSummary.total_committed)} color="amber" icon="⊕" style={{ border: '1px solid black' }} />
              <StatCard label="Actual Spent" value={fmt(costSummary.total_actual_spent)} color="red" icon="⊗" style={{ border: '1px solid black' }} />
              <StatCard label="Total Variations" value={fmt(costSummary.total_variations)} color="purple" icon="△" style={{ border: '1px solid black' }} />
              <StatCard label="Profit Margin" value={`${parseFloat(costSummary.profit_margin_pct || 0).toFixed(1)}%`}
                color={parseFloat(costSummary.profit_margin_pct) >= 0 ? 'green' : 'red'} icon="%" style={{ border: '1px solid black' }} />
            </div>
          ) : (
            <Card style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13, border: '1px solid black' }}>No cost summary available for this project.</Card>
          )}
        </div>
      </div>

      {/* DIVIDER */}
      <hr style={{ border: 'none', borderTop: '3px solid #94a3b8', margin: '0 0 32px 0' }} />

      {/* Project Modules */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 20 }}>
        Project Modules
      </div>

      {renderModuleView()}
    </div>
  );
}

export default function ProjectsPage({ toast, user }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [openingProject, setOpeningProject] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const projectStats = React.useMemo(() => {
    const stats = { total: projects.length, active: 0, planning: 0, completed: 0, on_hold: 0 };
    projects.forEach(p => {
      if (stats[p.status] !== undefined) stats[p.status]++;
    });
    return stats;
  }, [projects]);

  const filteredProjects = React.useMemo(() => {
    return projects.filter(p => {
      if (statusFilter && p.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.project_name?.toLowerCase().includes(q) &&
          !p.project_code?.toLowerCase().includes(q) &&
          !p.client_name?.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [projects, search, statusFilter]);

  const { projectId } = useParams();
  const navigate = useNavigate();
  const activeProjectId = projectId;

  const load = async () => {
    setLoading(true);
    try { const d = await api.listProjects(); setProjects(Array.isArray(d) ? d : []); } catch { }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleOpenProject = async (p) => {
    setOpeningProject(p.project_id);
    
    // Fetch data concurrently without the artificial spinner delay
    await Promise.allSettled([
      api.getCostSummary(p.project_id)
    ]);

    navigate(`/projects/${p.project_id}`);
    setOpeningProject(null);
  };

  if (activeProjectId) {
    const dashboardProject = projects.find(p => p.project_id === activeProjectId);
    if (dashboardProject) {
      return (
        <ProjectDashboardView
          project={dashboardProject}
          onBack={() => navigate('/projects')}
          toast={toast}
        />
      );
    }
  }

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    const errs = {};
    const req = ['project_name', 'project_code', 'client_name', 'location', 'contract_value', 'original_budget', 'contingency_budget', 'currency', 'vat_rate', 'wht_rate', 'status', 'contract_type', 'start_date', 'end_date'];
    req.forEach(k => {
      if (form[k] === undefined || form[k] === '') errs[k] = true;
    });
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.show('Please fill in all empty fields', 'error');
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      if (modal === 'edit') await api.updateProject(form.project_id, form);
      else await api.createProject(form);
      toast.show(modal === 'edit' ? 'Project updated' : 'Project created');
      setModal(null); load();
    } catch (e) { toast.show(e.message, 'error'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try { await api.deleteProject(id); toast.show('Project deleted'); load(); }
    catch (e) { toast.show(e.message, 'error'); }
  };



  const statusC = { planning: 'cyan', active: 'green', on_hold: 'amber', completed: 'green', cancelled: 'red' };

  return (
    <div className="fade-in">
      {openingProject && <RNLLoader fullScreen title="Opening Project..." subtitle="Preparing your workspace" />}
      <SectionHeader
        title="Projects"
      />

      {!loading && projects.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            <StatCard label="Total Projects" value={projectStats.total} color="accent" style={{ border: '1px solid black', background: '#cbccceff' }} />
            <StatCard label="Active Projects" value={projectStats.active} color="green" style={{ border: '1px solid black', background: '#cbccceff' }} />
            <StatCard label="In Planning" value={projectStats.planning} color="cyan" style={{ border: '1px solid black', background: '#cbccceff' }} />
            <StatCard label="Completed" value={projectStats.completed} color="purple" style={{ border: '1px solid black', background: '#cbccceff' }} />
          </div>

          <Card style={{ padding: '20px 24px', marginBottom: 24, borderRadius: 12, border: '1px solid black' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <Input label="Search Projects" placeholder="Search by name, code, or client..." value={search} onChange={v => setSearch(v)} />
              </div>
              <div style={{ width: 200 }}>
                <Select label="Status Filter" value={statusFilter} onChange={setStatusFilter} options={[{ value: '', label: 'All Statuses' }, ...STATUS_OPTS]} />
              </div>
              <div>
                <Btn variant="secondary" onClick={() => { setSearch(''); setStatusFilter(''); }} style={{ height: 42, background: '#cbccceff' }}>Clear Filters</Btn>
              </div>
            </div>
          </Card>
        </>
      )}

      {loading ? (
        <RNLLoader title="Loading Projects..." subtitle="Please wait" />
      ) : projects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏗</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No projects yet</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>Create your first project to get started.</div>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', border: '1px solid black', borderRadius: 12, background: 'var(--bg-card)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No projects match your filters</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>Try adjusting your search or status filter.</div>
          <Btn variant="secondary" onClick={() => { setSearch(''); setStatusFilter(''); }}>Clear Filters</Btn>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 14 }}>
          {filteredProjects.map(p => (
            <div key={p.project_id} style={{
              background: '#cbccceff',
              border: '2px solid #000',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#a9aaacff'; e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#cbccceff'; e.currentTarget.style.borderColor = '#000'; e.currentTarget.style.transform = 'none'; }}
              onClick={() => handleOpenProject(p)}
            >
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div style={{ flex: 1, paddingRight: 16 }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                      {p.project_name}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--accent)' }}>
                        {p.project_code}
                      </span>
                    </div>
                  </div>
                  <div style={{
                    background: p.status === 'active' ? '#10b981' : '#cbd5e1',
                    color: '#000', padding: '4px 12px', borderRadius: '4px', border: '1px solid #000',
                    fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    {p.status}
                  </div>
                </div>

              </div>

              <div style={{ marginTop: 'auto' }} />

              <div style={{ display: 'flex', borderTop: '2px solid #000' }} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => handleOpenProject(p)}
                  disabled={openingProject === p.project_id}
                  style={{ flex: 1, padding: '12px 0', background: '#3d6fe3ff', color: '#000', border: 'none', fontWeight: 800, fontSize: '13px', cursor: openingProject === p.project_id ? 'wait' : 'pointer', transition: 'filter 0.2s', opacity: openingProject === p.project_id ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.9)'}
                  onMouseLeave={e => e.currentTarget.style.filter = 'none'}
                >
                  {openingProject === p.project_id ? (
                    <>
                      <svg style={{ animation: 'spin 1s linear infinite', height: 14, width: 14 }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25"></circle>
                        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      OPENING
                    </>
                  ) : 'OPEN PROJECT'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal hideHeader onClose={() => setModal(null)} width={800}>
          <div style={{ margin: '-20px', display: 'flex', flexDirection: 'column', maxHeight: 'calc(92vh - 40px)' }}>
            <div style={{ flexShrink: 0, background: '#2B374A', color: 'white', padding: '20px 24px', borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {modal === 'create' ? 'New Project' : 'Edit Project'}
                </h2>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Main project details and codes are required for all system tracking</div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

              {/* SECTION 1 */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                SECTION 1 — PROJECT IDENTITY
              </div>
              <div style={{ background: 'var(--accent-dim)', padding: 16, borderRadius: 12, border: '1px solid rgba(59,130,246,0.1)', marginBottom: 24 }}>
                <FormGrid cols={2}>
                  <Input label="PROJECT NAME" required value={form.project_name || ''} onChange={set('project_name')} placeholder="e.g. Abuja Central Mixed-Use" style={{ border: errors.project_name ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="PROJECT CODE" required value={form.project_code || ''} onChange={set('project_code')} placeholder="e.g. ACMUD-001" style={{ border: errors.project_code ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="CLIENT NAME" value={form.client_name || ''} onChange={set('client_name')} style={{ border: errors.client_name ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="LOCATION" value={form.location || ''} onChange={set('location')} style={{ border: errors.location ? '1px solid var(--red)' : '1px solid black' }} />
                </FormGrid>
              </div>

              {/* SECTION 2 */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                SECTION 2 — FINANCIAL PARAMETERS
              </div>
              <div style={{ marginBottom: 24 }}>
                <FormGrid cols={3}>
                  <Input label="CONTRACT VALUE" type="number" value={form.contract_value || ''} onChange={set('contract_value')} style={{ border: errors.contract_value ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="ORIGINAL BUDGET" type="number" value={form.original_budget || ''} onChange={set('original_budget')} style={{ border: errors.original_budget ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="CONTINGENCY" type="number" value={form.contingency_budget || ''} onChange={set('contingency_budget')} style={{ border: errors.contingency_budget ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="CURRENCY" value={form.currency || 'NGN'} onChange={set('currency')} style={{ border: errors.currency ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="VAT RATE (%)" type="number" value={form.vat_rate || ''} onChange={set('vat_rate')} style={{ border: errors.vat_rate ? '1px solid var(--red)' : '1px solid black' }} />
                  <Input label="WHT RATE (%)" type="number" value={form.wht_rate || ''} onChange={set('wht_rate')} style={{ border: errors.wht_rate ? '1px solid var(--red)' : '1px solid black' }} />
                </FormGrid>
              </div>

              {/* SECTION 3 */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ background: '#2B374A', color: 'white', padding: '10px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  PROJECT EXECUTION & STATUS
                </div>
                <div style={{ padding: 16, background: 'var(--bg-card)' }}>
                  <FormGrid cols={3}>
                    <Select label="STATUS" value={form.status || 'planning'} onChange={set('status')} options={STATUS_OPTS} style={{ border: errors.status ? '1px solid var(--red)' : '1px solid black' }} />
                    <Select label="CONTRACT TYPE" value={form.contract_type || ''} onChange={set('contract_type')} options={CONTRACT_OPTS} style={{ border: errors.contract_type ? '1px solid var(--red)' : '1px solid black' }} />
                    <Input label="START DATE" type="date" value={form.start_date || ''} onChange={set('start_date')} style={{ border: errors.start_date ? '1px solid var(--red)' : '1px solid black' }} />
                    <Input label="END DATE" type="date" value={form.end_date || ''} onChange={set('end_date')} style={{ border: errors.end_date ? '1px solid var(--red)' : '1px solid black' }} />
                  </FormGrid>
                  <div style={{ marginTop: 14 }}>
                    <Textarea label="DESCRIPTION" value={form.description || ''} onChange={set('description')} rows={2} placeholder="Optional notes or description about the project scope..." style={{ border: '1px solid black' }} />
                  </div>
                </div>
              </div>

            </div>

            {/* FOOTER ACTIONS */}
            <div style={{ flexShrink: 0, background: '#1e293b', padding: '16px 24px', borderTop: '1px solid var(--border)', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn
                  onClick={save}
                  loading={saving}
                  size="md"
                  style={{ background: '#10b981', color: 'white', border: 'none', fontWeight: 600, padding: '10px 24px', borderRadius: 8, transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#059669'}
                  onMouseLeave={e => e.currentTarget.style.background = '#10b981'}
                >
                  {modal === 'create' ? '+ Create Project' : 'Save Changes'}
                </Btn>
              </div>
              <Btn onClick={() => setModal(null)} style={{ padding: '8px 20px', borderRadius: 8, background: 'var(--red)', color: 'white', fontWeight: 600, border: 'none', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#dc2626'} onMouseLeave={e => e.currentTarget.style.background = 'var(--red)'}>Cancel</Btn>
            </div>

          </div>
        </Modal>
      )}
    </div>
  );
}
